import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import { downloadPDF } from "../../receipts/services/receiptService";
import { overlayClose } from "../../../../shared/utils/overlayClose";
import { ColorSwatches, COLOR_DEFAULT } from "../../../../shared/components/ColorSwatches";

/**
 * Modal de Estado de Cuenta PDF.
 * Diseño igual al FirmaModal de EstadoFinanciero: segmented buttons para
 * moneda (CRC / USD) e idioma (ES / EN).
 *
 * Props:
 *   onClose()         — cerrar el modal
 *   filialPreselected — { id, idFilial } si ya se sabe la filial (desde el botón de la fila)
 */
export default function PdfEstadoModal({ onClose, filialPreselected = null }) {
  const { t } = useTranslation();
  const hoy = new Date();
  const anoInicio = `${hoy.getFullYear()}-01-01`;
  const fechaHoy  = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}-${String(hoy.getDate()).padStart(2, "0")}`;

  const [filiales,    setFiliales]    = useState([]);
  const [filial,      setFilial]      = useState("");
  const [fechaInicio, setFechaInicio] = useState(anoInicio);
  const [fechaFinal,  setFechaFinal]  = useState(fechaHoy);
  const [moneda,      setMoneda]      = useState("CRC");
  const [idioma,      setIdioma]      = useState("es");
  const [color,       setColor]       = useState(COLOR_DEFAULT);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState("");

  const MONEDAS = [
    { key: "CRC", icon: "₡", label: "Colones",  borderRight: true },
    { key: "USD", icon: "$", label: "Dólares",   borderRight: false },
  ];
  const IDIOMAS = [
    { key: "es", flag: "🇨🇷", label: "Español", borderRight: true },
    { key: "en", flag: "🇺🇸", label: "English",  borderRight: false },
  ];

  useEffect(() => {
    apiFetch("/api/residents/filial/")
      .then((r) => r.json())
      .then((data) => {
        const results = data.results ?? data;
        setFiliales(results);
        if (filialPreselected) {
          setFilial(String(filialPreselected.id));
        } else if (results.length > 0) {
          setFilial(String(results[0].id));
        }
      })
      .catch(() => {});
  }, [filialPreselected]);

  useEffect(() => {
    apiFetch("/api/residents/condominio/")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.color_reporte) setColor(d.color_reporte); })
      .catch(() => {});
  }, []);

  async function handleGenerate() {
    if (!filial || !fechaInicio || !fechaFinal) {
      setError(t("recibos.camposObligatorios"));
      return;
    }
    setError("");
    setLoading(true);
    try {
      const blob = await downloadPDF({
        filial,
        fecha_inicio: fechaInicio,
        fecha_final:  fechaFinal,
        moneda,
        idioma,
        color,
      });
      const url = window.URL.createObjectURL(blob);
      const a   = document.createElement("a");
      a.href    = url;
      const label = filiales.find((f) => String(f.id) === filial)?.idFilial ?? filial;
      a.download  = `estado_cuenta_${label}_${fechaInicio.replace(/\//g, "-")}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      onClose();
    } catch {
      setError(t("recibos.errorGenerarPdf"));
    } finally {
      setLoading(false);
    }
  }

  const inputStyle = {
    width: "100%",
    padding: "8px 10px",
    background: "var(--bg-input)",
    border: "1px solid var(--border-card)",
    borderRadius: 6,
    color: "var(--text-primary)",
    fontSize: "0.85rem",
    colorScheme: "light dark",
    outline: "none",
    transition: "border-color 0.15s",
    boxSizing: "border-box",
  };

  const sectionLabel = {
    fontSize: "0.67rem",
    fontWeight: 700,
    letterSpacing: "1.1px",
    textTransform: "uppercase",
    color: "var(--text-muted)",
    marginBottom: 8,
  };

  return (
    <>
      {/* Overlay */}
      <div
        {...overlayClose(onClose)}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.72)",
          backdropFilter: "blur(5px)",
          zIndex: 1000,
        }}
      />

      {/* Modal */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "fixed", top: "50%", left: "50%",
          transform: "translate(-50%,-50%)",
          background: "var(--bg-card)",
          border: "1px solid var(--border-card)",
          borderRadius: 16,
          width: "min(500px,95vw)",
          zIndex: 1001,
          boxShadow: "0 32px 80px rgba(0,0,0,0.65)",
          overflow: "hidden",
        }}
      >
        {/* ── Header ── */}
        <div style={{
          background: "linear-gradient(135deg,rgba(249,115,22,0.13) 0%,rgba(249,115,22,0.04) 100%)",
          borderBottom: "1px solid var(--border-card)",
          padding: "18px 22px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 13 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 11, flexShrink: 0,
              background: "rgba(249,115,22,0.14)",
              border: "1px solid rgba(249,115,22,0.28)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <i className="fa-solid fa-file-pdf" style={{ color: "#f97316", fontSize: "1.15rem" }} />
            </div>
            <div>
              <div style={{ fontSize: "1rem", fontWeight: 700, color: "var(--text-primary)", lineHeight: 1.2 }}>
                {t("recibos.estadoCuenta")}
              </div>
              <div style={{ fontSize: "0.74rem", color: "var(--text-muted)", marginTop: 2 }}>
                {t("reportes.configuraPDF")}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: "var(--text-muted)", fontSize: "1.1rem",
              padding: "4px 6px", borderRadius: 6, lineHeight: 1,
            }}
            onMouseOver={(e) => (e.currentTarget.style.color = "var(--text-primary)")}
            onMouseOut={(e)  => (e.currentTarget.style.color = "var(--text-muted)")}
          >
            ✕
          </button>
        </div>

        {/* ── Body ── */}
        <div style={{ padding: "22px 24px", display: "flex", flexDirection: "column", gap: 18 }}>

          {/* Filial */}
          <div>
            <div style={sectionLabel}>{t("common.filial")}</div>
            <select
              value={filial}
              onChange={(e) => setFilial(e.target.value)}
              style={{ ...inputStyle, cursor: "pointer" }}
            >
              {filiales.length === 0 && (
                <option value="">{t("invoices.sinFiliales")}</option>
              )}
              {filiales.map((f) => (
                <option key={f.id} value={String(f.id)} style={{ background: "var(--bg-card)" }}>
                  {f.idFilial}
                </option>
              ))}
            </select>
          </div>

          {/* Fechas */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <div style={sectionLabel}>{t("recibos.fechaInicio")}</div>
              <input
                type="date"
                value={fechaInicio}
                onChange={(e) => {
                  setFechaInicio(e.target.value);
                  if (e.target.value) {
                    const [y, m] = e.target.value.split("-").map(Number);
                    const fin = new Date(y, m, 0);
                    setFechaFinal(
                      `${y}-${String(m).padStart(2, "0")}-${String(fin.getDate()).padStart(2, "0")}`
                    );
                  }
                }}
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = "#f97316")}
                onBlur={(e)  => (e.target.style.borderColor = "var(--border-card)")}
              />
            </div>
            <div>
              <div style={sectionLabel}>{t("recibos.fechaFinal")}</div>
              <input
                type="date"
                value={fechaFinal}
                onChange={(e) => setFechaFinal(e.target.value)}
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = "#f97316")}
                onBlur={(e)  => (e.target.style.borderColor = "var(--border-card)")}
              />
            </div>
          </div>

          {/* Divider */}
          <div style={{ borderTop: "1px solid var(--border-card)", margin: "0 -2px" }} />

          {/* Moneda */}
          <div>
            <div style={sectionLabel}>{t("reportes.monedaReporte")}</div>
            <div style={{
              display: "flex", borderRadius: 9,
              border: "1px solid var(--border-card)", overflow: "hidden",
            }}>
              {MONEDAS.map((opt) => {
                const active = moneda === opt.key;
                return (
                  <button
                    key={opt.key}
                    onClick={() => setMoneda(opt.key)}
                    style={{
                      flex: 1, padding: "10px 0", border: "none",
                      borderRight: opt.borderRight ? "1px solid var(--border-card)" : "none",
                      background: active ? "rgba(249,115,22,0.14)" : "transparent",
                      color: active ? "#f97316" : "var(--text-muted)",
                      fontWeight: active ? 700 : 400,
                      fontSize: "0.88rem", cursor: "pointer", transition: "all 0.15s",
                    }}
                  >
                    <span style={{ marginRight: 5 }}>{opt.icon}</span>{opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Idioma */}
          <div>
            <div style={sectionLabel}>{t("reportes.idiomaReporte")}</div>
            <div style={{
              display: "flex", borderRadius: 9,
              border: "1px solid var(--border-card)", overflow: "hidden",
            }}>
              {IDIOMAS.map((opt) => {
                const active = idioma === opt.key;
                return (
                  <button
                    key={opt.key}
                    onClick={() => setIdioma(opt.key)}
                    style={{
                      flex: 1, padding: "10px 0", border: "none",
                      borderRight: opt.borderRight ? "1px solid var(--border-card)" : "none",
                      background: active ? "rgba(99,102,241,0.13)" : "transparent",
                      color: active ? "#818cf8" : "var(--text-muted)",
                      fontWeight: active ? 700 : 400,
                      fontSize: "0.88rem", cursor: "pointer", transition: "all 0.15s",
                    }}
                  >
                    <span style={{ marginRight: 6 }}>{opt.flag}</span>{opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Color de marca */}
          <div>
            <div style={sectionLabel}>{t("reportes.colorReporte")}</div>
            <ColorSwatches value={color} onChange={setColor} />
          </div>

          {/* Error */}
          {error && (
            <div style={{
              background: "rgba(248,113,113,0.1)",
              border: "1px solid rgba(248,113,113,0.3)",
              borderLeft: "3px solid #f87171",
              borderRadius: 6, padding: "8px 12px",
              color: "#fca5a5", fontSize: "0.82rem",
            }}>
              <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 5 }} />
              {error}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div style={{
          borderTop: "1px solid var(--border-card)",
          background: "var(--hover-bg)",
          padding: "14px 24px",
          display: "flex", justifyContent: "flex-end", gap: 10,
        }}>
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              padding: "9px 18px", background: "transparent",
              border: "1px solid var(--border-card)", borderRadius: 8,
              color: "var(--text-muted)", fontSize: "0.85rem", cursor: "pointer",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.borderColor = "var(--text-secondary)";
              e.currentTarget.style.color = "var(--text-secondary)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.borderColor = "var(--border-card)";
              e.currentTarget.style.color = "var(--text-muted)";
            }}
          >
            {t("common.cancelar")}
          </button>
          <button
            onClick={handleGenerate}
            disabled={loading || filiales.length === 0}
            style={{
              padding: "9px 22px",
              background: loading ? "rgba(249,115,22,0.6)" : "#f97316",
              color: "#fff", border: "none", borderRadius: 8,
              fontWeight: 700, fontSize: "0.85rem",
              cursor: loading ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", gap: 8,
              boxShadow: loading ? "none" : "0 4px 16px rgba(249,115,22,0.38)",
            }}
            onMouseOver={(e) => { if (!loading) e.currentTarget.style.background = "#ea6b0c"; }}
            onMouseOut={(e)  => { if (!loading) e.currentTarget.style.background = "#f97316"; }}
          >
            {loading ? (
              <>
                <i className="fa-solid fa-circle-notch fa-spin" /> {t("common.cargando")}
              </>
            ) : (
              <>
                <i className="fa-solid fa-file-pdf" /> {t("reportes.generarPDFBtn")}
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
}
